/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography,
  Button,
  Collapse,
  CollapseProps
} from 'antd';
import type { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getDisplayTypes,
  watchDisplayTypes
} from '@/redux/slices/banner-header.slice';

type BannerHeaderSearcherProps = {
  onSearch: (value: FilterBannerHeader) => void;
};

type BannerHeaderFilterFormValues = {
  title?: string;
  displayType?: number | undefined;
  updatedFrom?: Dayjs | undefined;
  updatedTo?: Dayjs | undefined;
};

export function BannerHeaderSearcher({ onSearch }: BannerHeaderSearcherProps) {
  const [form] = Form.useForm<BannerHeaderFilterFormValues>();
  const dispatch = useAppDispatch();
  const displayTypes = useAppSelector(watchDisplayTypes);

  const startDate = Form.useWatch('updatedFrom', form);
  const endDate = Form.useWatch('updatedTo', form);

  useEffect(() => {
    dispatch(getDisplayTypes());
  }, [dispatch]);

  const handleSearch = () => {
    const values = form.getFieldsValue();
    const payload: FilterBannerHeader = {
      title: values.title,
      displayType: values.displayType,
      updatedFrom: values.updatedFrom
        ? values.updatedFrom.format('YYYY-MM-DD')
        : undefined,
      updatedTo: values.updatedTo
        ? values.updatedTo.format('YYYY-MM-DD')
        : undefined
    };
    onSearch(payload);
  };

  const handleReset = () => {
    form.resetFields();
    onSearch({});
  };

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: (
        <Typography.Title css={titleStyles} level={5}>
          BỘ LỌC
        </Typography.Title>
      ),
      children: (
        <Form form={form} css={formStyles}>
          <Row gutter={24} align='stretch'>
            <Col span={8} css={filterItem}>
              <Form.Item
                label='Tiêu đề Banner Header'
                name='title'
                css={customLabel}
              >
                <Input placeholder='Nhập tiêu đề Banner Header' allowClear />
              </Form.Item>
            </Col>
            <Col span={6} css={filterItem}>
              <Form.Item
                label='Kiểu hiển thị'
                name='displayType'
                css={customLabel}
              >
                <Select
                  allowClear
                  placeholder='Chọn kiểu hiển thị'
                  options={displayTypes.map((dt) => ({
                    value: dt.id,
                    label: dt.name
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={10} css={[timeStyle, filterItem]}>
              <Form.Item
                label='Thời gian cập nhật'
                style={{ width: '100%' }}
                css={customLabel}
              >
                <Row
                  gutter={8}
                  style={{
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Col span={11}>
                    <Form.Item name='updatedFrom' style={{ width: '100%' }}>
                      <DatePicker
                        format='DD/MM/YYYY'
                        style={{ width: '100%' }}
                        disabledDate={(current) =>
                          endDate
                            ? current && current > endDate.endOf('day')
                            : false
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col style={{ textAlign: 'center' }}> - </Col>
                  <Col span={11}>
                    <Form.Item name='updatedTo' style={{ width: '100%' }}>
                      <DatePicker
                        format='DD/MM/YYYY'
                        style={{ width: '100%' }}
                        disabledDate={(current) =>
                          startDate
                            ? current && current < startDate.startOf('day')
                            : false
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>

          <Row
            gutter={16}
            align='middle'
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '2rem'
            }}
          >
            <Button
              type='default'
              icon={<ReloadOutlined />}
              css={resetButton}
              onClick={handleReset}
            >
              Đặt lại
            </Button>
            <Button
              type='primary'
              icon={<SearchOutlined />}
              css={searchButton}
              onClick={handleSearch}
            >
              Tìm kiếm
            </Button>
          </Row>
        </Form>
      )
    }
  ];

  return (
    <Row justify='space-between' align='middle'>
      <Collapse
        items={items}
        defaultActiveKey={'1'}
        expandIconPosition={'end'}
        css={collapseStyles}
      />
    </Row>
  );
}

const collapseStyles = css`
  width: 100%;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  background-color: transparent !important;

  .ant-collapse-header {
    width: fit-content;
    border-radius: 0.8rem !important;
    border: none !important;
    padding: 0 !important;

    .ant-collapse-expand-icon {
      align-self: center;
    }
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border: none !important;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
  font-weight: 700 !important;
`;

const formStyles = css`
  margin-top: 0.5rem;
  padding: 1rem 0;
`;

const filterItem = css`
  display: flex;
  justify-content: start !important;
  gap: 0.5rem;

  label {
    height: 100% !important;
  }

  .ant-form-item-label {
    width: fit-content;
    max-width: 33.33%;
    height: 100% !important;
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  input,
  .ant-select,
  .ant-picker {
    flex: 1;
    min-width: 6rem;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;
    &::after,
    &::before {
      display: none !important;
    }

    margin: 0.4rem 2rem 0 0 !important;
  }

  width: 100%;
  margin-bottom: 0;
`;

const resetButton = css`
  margin-right: 10px;
  color: #007bff;
  border-color: #007bff;
`;

const searchButton = css`
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
`;

const timeStyle = css`
  .ant-form-item {
    margin-bottom: unset !important;
  }

  .ant-row {
    margin-left: unset !important;
    margin-right: unset !important;
  }

  .ant-col .ant-col-11 {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
`;
